// Iter 82.7 / Phase E2.3 — Monthly Attendance Summary Report.
// Per-employee × month table with all 12 operator-specified columns,
// heat-map bands (red <85 / amber 85-95 / green ≥95), reliability score
// + classification, drill-down to snapshots, CSV + XLSX exports.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DownloadSimple, FileXls, FunnelSimple, ArrowSquareOut } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";

const CLASS_TONE = {
    Excellent: "bg-emerald-100 text-emerald-800",
    Good: "bg-sky-100 text-sky-800",
    "Needs Attention": "bg-amber-100 text-amber-800",
    Critical: "bg-rose-100 text-rose-800",
};

function pctBg(pct) {
    if (pct >= 95) return "bg-emerald-100 text-emerald-800";
    if (pct >= 85) return "bg-amber-100 text-amber-800";
    return "bg-rose-100 text-rose-800";
}

export default function AttendanceMonthlySummaryReport() {
    const today = new Date();
    const [filters, setFilters] = useState({
        year: today.getFullYear(),
        month: today.getMonth() + 1,
        department: "",
        employee_id: "",
    });
    const [data, setData] = useState({ items: [], summary: {} });
    const [loading, setLoading] = useState(true);
    const [hrMeta, setHrMeta] = useState({ departments: [] });

    const loadMeta = useCallback(async () => {
        try {
            const hr = await api.get("/hr/employee/meta");
            setHrMeta(hr.data);
        } catch (e) { /* meta non-blocking */ }
    }, []);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            const { data: payload } = await api.get("/attendance/report/monthly-summary", { params });
            setData(payload);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load monthly summary"));
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { Promise.resolve().then(loadMeta); }, [loadMeta]);
    useEffect(() => { reload(); }, [reload]);

    const download = async (ext) => {
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            const { data: blob } = await api.get(`/attendance/report/monthly-summary/export.${ext}`, { params, responseType: "blob" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `attendance-monthly-${filters.year}-${String(filters.month).padStart(2, "0")}.${ext}`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err, `${ext.toUpperCase()} export failed`));
        }
    };

    const s = data.summary || {};

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1700px]" data-testid="att-monthly-page">
            <PageHeader
                eyebrow="Attendance Reports"
                title="Monthly Attendance Summary"
                description={`Per-employee aggregation for ${filters.year}-${String(filters.month).padStart(2, "0")} · ${data.total || 0} employee${data.total === 1 ? "" : "s"}`}
                actions={(
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => download("csv")} data-testid="att-monthly-csv-btn">
                            <DownloadSimple size={14} className="mr-1" /> CSV
                        </Button>
                        <Button size="sm" onClick={() => download("xlsx")} data-testid="att-monthly-xlsx-btn">
                            <FileXls size={14} className="mr-1" /> XLSX
                        </Button>
                    </div>
                )}
            />

            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4" data-testid="att-monthly-filters">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    <div>
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Year</Label>
                        <Input type="number" min="2020" max="2099" value={filters.year}
                               onChange={(e) => setFilters({ ...filters, year: parseInt(e.target.value, 10) })}
                               data-testid="att-monthly-year" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Month</Label>
                        <select className="w-full border rounded-md text-sm h-9 px-2" value={filters.month}
                                onChange={(e) => setFilters({ ...filters, month: parseInt(e.target.value, 10) })}
                                data-testid="att-monthly-month">
                            {Array.from({ length: 12 }).map((_, i) => (
                                <option key={i + 1} value={i + 1}>{new Date(2020, i, 1).toLocaleString("en", { month: "long" })}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Department</Label>
                        <select className="w-full border rounded-md text-sm h-9 px-2" value={filters.department}
                                onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                                data-testid="att-monthly-dept">
                            <option value="">All</option>
                            {(hrMeta.departments || []).map((d, i) => {
                                const name = typeof d === "string" ? d : (d.name || "");
                                const key = typeof d === "string" ? d : (d.id || d.code || d.name || `dep-${i}`);
                                return <option key={key} value={name}>{name}</option>;
                            })}
                        </select>
                    </div>
                    <div className="flex items-end">
                        <Button variant="outline" size="sm" className="w-full"
                                onClick={() => setFilters({ year: today.getFullYear(), month: today.getMonth() + 1, department: "", employee_id: "" })}
                                data-testid="att-monthly-clear">
                            <FunnelSimple size={14} className="mr-1" /> Clear
                        </Button>
                    </div>
                </div>
            </section>

            <section className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="att-monthly-kpis">
                <Kpi label="Employees" value={s.total_employees || 0} tone="slate" testid="kpi-total" />
                <Kpi label="Avg Attendance %" value={(s.avg_attendance_pct ?? 0).toFixed(1)} suffix="%" tone="sky" testid="kpi-avg" />
                <Kpi label="🟢 Green ≥95%" value={s.green_count || 0} tone="emerald" testid="kpi-green" />
                <Kpi label="🟡 Amber 85-95%" value={s.amber_count || 0} tone="amber" testid="kpi-amber" />
                <Kpi label="🔴 Red <85%" value={s.red_count || 0} tone="rose" testid="kpi-red" />
                <Kpi label="Total Late" value={s.total_late_count || 0} tone="orange" testid="kpi-late" />
            </section>

            {/* Iter 98 / Phase E2.4 — Desktop Agent rollup tiles.
                Surfaces only when at least one bridged employee exists in the period. */}
            {(s.total_desktop_agent_days > 0 || s.total_agent_active_hours > 0) && (
                <section className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="att-monthly-agent-kpis">
                    <Kpi label="Agent Days" value={s.total_desktop_agent_days || 0} tone="violet" testid="kpi-agent-days" />
                    <Kpi label="Active Hrs" value={(s.total_agent_active_hours ?? 0).toFixed(1)} suffix="h" tone="emerald" testid="kpi-active" />
                    <Kpi label="Idle Hrs" value={(s.total_agent_idle_hours ?? 0).toFixed(1)} suffix="h" tone="amber" testid="kpi-idle" />
                    <Kpi label="Locked Hrs" value={(s.total_agent_locked_hours ?? 0).toFixed(1)} suffix="h" tone="rose" testid="kpi-locked" />
                    <Kpi label="Needs Review" value={s.total_needs_review_days || 0} tone="amber" testid="kpi-needs-review" />
                </section>
            )}

            <section className="mt-4 bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="att-monthly-table">
                {loading ? (
                    <div className="text-center py-8 text-sm text-slate-400">Loading…</div>
                ) : data.items.length === 0 ? (
                    <div className="text-center py-12 text-sm text-slate-400">No records for the selected period.</div>
                ) : (
                    <table className="w-full text-sm min-w-[1700px]">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Employee</th>
                                <th className="text-left px-3 py-2 font-medium">Department</th>
                                <th className="text-right px-3 py-2 font-medium">Working</th>
                                <th className="text-right px-3 py-2 font-medium">Present</th>
                                <th className="text-right px-3 py-2 font-medium">Absent</th>
                                <th className="text-right px-3 py-2 font-medium">Half</th>
                                <th className="text-right px-3 py-2 font-medium">Leave</th>
                                <th className="text-right px-3 py-2 font-medium">Holidays</th>
                                <th className="text-right px-3 py-2 font-medium">W.Off</th>
                                <th className="text-right px-3 py-2 font-medium">Att %</th>
                                <th className="text-right px-3 py-2 font-medium">Net Hrs</th>
                                <th className="text-right px-3 py-2 font-medium">Late</th>
                                <th className="text-right px-3 py-2 font-medium">Early Dep</th>
                                <th className="text-right px-3 py-2 font-medium">Regularized</th>
                                {/* Iter 98 — Desktop Agent rollup columns. */}
                                <th className="text-right px-3 py-2 font-medium" title="Days where attendance was sourced from Desktop Agent">Agent Days</th>
                                <th className="text-right px-3 py-2 font-medium" title="Total active hours from Desktop Agent">Active Hrs</th>
                                <th className="text-right px-3 py-2 font-medium" title="Total idle hours from Desktop Agent">Idle Hrs</th>
                                <th className="text-right px-3 py-2 font-medium" title="Total locked hours from Desktop Agent">Locked Hrs</th>
                                <th className="text-right px-3 py-2 font-medium" title="Average daily active minutes (across days with activity)">Avg/Day</th>
                                <th className="text-right px-3 py-2 font-medium" title="Days flagged Needs Review">Review</th>
                                <th className="text-center px-3 py-2 font-medium">Reliability</th>
                                <th className="text-left px-3 py-2 font-medium">Drill</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map((r) => (
                                <tr key={r.employee_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`monthly-row-${r.employee_id}`}>
                                    <td className="px-3 py-2">
                                        <div className="text-sm font-semibold">{r.employee_name}</div>
                                        <div className="text-xs text-slate-500 font-mono">{r.employee_code}</div>
                                    </td>
                                    <td className="px-3 py-2 text-xs">{r.department || "—"}</td>
                                    <td className="px-3 py-2 text-right">{r.working_days}</td>
                                    <td className="px-3 py-2 text-right text-emerald-700">{r.present_days}</td>
                                    <td className="px-3 py-2 text-right text-rose-700">{r.absent_days}</td>
                                    <td className="px-3 py-2 text-right text-amber-700">{r.half_days}</td>
                                    <td className="px-3 py-2 text-right text-sky-700">{r.leave_days}</td>
                                    <td className="px-3 py-2 text-right text-indigo-700">{r.holidays}</td>
                                    <td className="px-3 py-2 text-right text-slate-500">{r.weekly_offs}</td>
                                    <td className="px-3 py-2 text-right">
                                        <Badge className={`text-xs font-bold ${pctBg(r.attendance_pct)}`} data-testid={`monthly-pct-${r.employee_id}`}>{r.attendance_pct.toFixed(1)}%</Badge>
                                    </td>
                                    <td className="px-3 py-2 text-right">{r.net_working_hours.toFixed(1)}</td>
                                    <td className="px-3 py-2 text-right text-orange-700">{r.late_count}</td>
                                    <td className="px-3 py-2 text-right text-orange-700">{r.early_departure_count}</td>
                                    <td className="px-3 py-2 text-right text-violet-700">{r.regularized_days}</td>
                                    {/* Iter 98 — agent columns */}
                                    <td className="px-3 py-2 text-right text-violet-700" data-testid={`monthly-agent-days-${r.employee_id}`}>{r.desktop_agent_days || 0}</td>
                                    <td className="px-3 py-2 text-right text-emerald-700">{(r.agent_total_active_hours || 0).toFixed(1)}</td>
                                    <td className="px-3 py-2 text-right text-amber-700">{(r.agent_total_idle_hours || 0).toFixed(1)}</td>
                                    <td className="px-3 py-2 text-right text-rose-700">{(r.agent_total_locked_hours || 0).toFixed(1)}</td>
                                    <td className="px-3 py-2 text-right text-slate-700 font-mono text-xs">{Math.round(r.avg_daily_active_minutes || 0)}m</td>
                                    <td className="px-3 py-2 text-right" data-testid={`monthly-needs-review-${r.employee_id}`}>
                                        {(r.needs_review_days || 0) > 0
                                            ? <Badge className="bg-amber-100 text-amber-800 text-xs">{r.needs_review_days}</Badge>
                                            : <span className="text-slate-300">—</span>}
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                        <div className="flex flex-col items-center gap-0.5">
                                            <span className="text-sm font-bold text-slate-900" data-testid={`monthly-score-${r.employee_id}`}>{r.reliability_score.toFixed(1)}</span>
                                            <Badge className={`text-[10px] ${CLASS_TONE[r.reliability_classification] || "bg-slate-100"}`} data-testid={`monthly-class-${r.employee_id}`}>{r.reliability_classification}</Badge>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <Link to={`/admin/attendance/snapshots?employee_id=${r.employee_id}&from_date=${data.from_date}&to_date=${data.to_date}`}
                                              className="text-xs text-sky-700 hover:underline flex items-center gap-1" data-testid={`monthly-drill-${r.employee_id}`}>
                                            <ArrowSquareOut size={12} /> Drill
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>
        </div>
    );
}

function Kpi({ label, value, suffix = "", tone = "slate", testid }) {
    const map = {
        slate: "bg-slate-50 border-slate-200 text-slate-700",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
        rose: "bg-rose-50 border-rose-200 text-rose-900",
        amber: "bg-amber-50 border-amber-200 text-amber-900",
        sky: "bg-sky-50 border-sky-200 text-sky-900",
        orange: "bg-orange-50 border-orange-200 text-orange-900",
        violet: "bg-violet-50 border-violet-200 text-violet-900",
    };
    return (
        <div className={`rounded-lg border p-3 ${map[tone]}`} data-testid={testid}>
            <div className="text-[9px] uppercase tracking-wider font-semibold">{label}</div>
            <div className="text-xl font-bold mt-1">{value}{suffix && <span className="text-xs font-normal opacity-70 ml-0.5">{suffix}</span>}</div>
        </div>
    );
}
